/**
 * Per-node contract registry. Each entry binds a node type to its
 * input / config / output Zod schemas so callers can validate IO shape
 * regardless of where the node executes.
 *
 * Registry is intentionally additive — only nodes that have been migrated
 * to strict contracts appear here. Older nodes still rely on
 * `ACTION_CONFIG_REQUIREMENTS` until they're decomposed.
 */

import type { z } from "zod";

import {
  createTaskConfigSchema,
  createTaskInputSchema,
  createTaskOutputSchema,
} from "./create-task";
import {
  humanReviewConfigSchema,
  humanReviewInputSchema,
  humanReviewOutputSchema,
} from "./human-review";
import { runAgentConfigSchema, runAgentInputSchema, runAgentOutputSchema } from "./run-agent";
import { sendEmailConfigSchema, sendEmailInputSchema, sendEmailOutputSchema } from "./send-email";
import { tagActionConfigSchema, tagActionInputSchema, tagActionOutputSchema } from "./tag-actions";
import { TRIGGER_NODE_CONTRACTS } from "./triggers";

export type NodeContract = {
  inputSchema: z.ZodType<unknown>;
  configSchema: z.ZodType<unknown>;
  outputSchema: z.ZodType<unknown>;
};

const STATIC_NODE_CONTRACTS: Record<string, NodeContract> = {
  add_tag: {
    inputSchema: tagActionInputSchema,
    configSchema: tagActionConfigSchema,
    outputSchema: tagActionOutputSchema,
  },
  remove_tag: {
    inputSchema: tagActionInputSchema,
    configSchema: tagActionConfigSchema,
    outputSchema: tagActionOutputSchema,
  },
  create_task: {
    inputSchema: createTaskInputSchema,
    configSchema: createTaskConfigSchema,
    outputSchema: createTaskOutputSchema,
  },
  human_review: {
    inputSchema: humanReviewInputSchema,
    configSchema: humanReviewConfigSchema,
    outputSchema: humanReviewOutputSchema,
  },
  send_email: {
    inputSchema: sendEmailInputSchema,
    configSchema: sendEmailConfigSchema,
    outputSchema: sendEmailOutputSchema,
  },
  run_agent: {
    inputSchema: runAgentInputSchema,
    configSchema: runAgentConfigSchema,
    outputSchema: runAgentOutputSchema,
  },
};

export const NODE_CONTRACTS: Record<string, NodeContract> = {
  ...TRIGGER_NODE_CONTRACTS,
  ...STATIC_NODE_CONTRACTS,
};

export function getNodeContract(type: string): NodeContract | undefined {
  return NODE_CONTRACTS[type];
}

export * from "./create-task";
export * from "./dataflow";
export * from "./human-review";
export * from "./review-question";
export * from "./run-agent";
export * from "./send-email";
export * from "./tag-actions";
export * from "./triggers";
